import { useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import type {
  GetDashboardCmaReportsQuery,
  GetDashboardCmaReportsQueryVariables,
} from '@graphql/__generated__/graphql';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import CreateCMAReport from '@modules/create-cma-report/CreateCMAReport';
import { DashboardCMACardItem } from '@modules/dashboard/dashboard-cma/DashboardCMACardItem';
import { GET_DASHBOARD_CMA_REPORTS } from '@modules/dashboard/dashboardQueries';
import { useDashboardList } from '@modules/dashboard/hooks/useDashboardList';
import { DashboardCard } from '@modules/dashboard/shared/DashboardCard';
import { useLocale } from '@shared/hooks/locale';

export const DashboardCMACard = () => {
  const { t } = useLocale();
  const { me } = useAppData();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const queryResult = useDashboardList<
    GetDashboardCmaReportsQuery,
    GetDashboardCmaReportsQueryVariables,
    GetDashboardCmaReportsQuery['cma_reports'][number]
  >({
    query: GET_DASHBOARD_CMA_REPORTS,
    variables: {
      where: {
        created_by: { _eq: me?.id },
      },
    },
    dataKey: 'cma_reports',
    countKey: 'cma_reports_aggregate',
    fetchPolicy: 'network-only',
  });

  const handleCreateCMA = (newCMAReportId: string) => {
    setIsCreateDialogOpen(false);
    const targetSearchParams = new URLSearchParams(searchParams);
    targetSearchParams.set('language', me?.language ?? 'en');
    navigate({
      pathname: `/cma-reports/${newCMAReportId}/page-cover`,
      search: targetSearchParams.toString(),
    });
  };

  return (
    <>
      <DashboardCard
        title={t('CMA reports')}
        actionLabel={t('View all CMA reports')}
        actionLink="/cma-reports"
        queryResult={queryResult}
        renderItem={(cmaReport, key) => (
          <DashboardCMACardItem key={key} cmaReport={cmaReport} />
        )}
        emptyStateMessage={t('You have not created any CMA reports yet,')}
        emptyStateActionLabel={t('create your first one')}
        onEmptyStateAction={() => setIsCreateDialogOpen(true)}
        hideBadge
      />

      {isCreateDialogOpen && (
        <CreateCMAReport
          onClose={() => setIsCreateDialogOpen(false)}
          onCreated={handleCreateCMA}
        />
      )}
    </>
  );
};
